export interface PbhSort {
  sortby: string;
  sortorder: "asc" | "desc";
}

export interface GetPbhOrdersArgs {
  rows: number;
  skip: number;
  sort?: PbhSort;
  /**
   * Action status filter (e.g., "Closed", "Open", "Waiting", "Action", "Cancelled")
   * OData `$filter` on Raw* fields when supported; otherwise client refinement.
   */
  action?: string;
  /**
   * User object required when using action filtering
   * Used to determine company-specific filtering logic (e.g., AJW vs external users)
   */
  user?: Record<string, unknown>;
  /**
   * Property name to filter by (e.g., "CustomerPO", "PartNumber", "CompanyName")
   * When provided along with `query`, uses `orderQueryODataSqlOnly`
   */
  property?: string;
  /**
   * Search query value to filter by
   * When provided along with `property`, uses `orderQueryODataSqlOnly`
   */
  query?: string | null;
  /**
   * Inbound status filter (e.g., "Awaiting OffCore Info", "OffCore Part Info")
   * Can be combined with property/query filters, but NOT with outboundStatus
   * (only one status filter can be active at a time)
   */
  inboundStatus?: string | null;
  /**
   * Outbound status filter (e.g., "Shipped", "Cancelled")
   * Can be combined with property/query filters, but NOT with inboundStatus
   * (only one status filter can be active at a time)
   */
  outboundStatus?: string | null;
  /**
   * When false, the API skips requesting total count ($count=true).
   * Use for list views that use overfetch-by-one to detect "has next page".
   * Default true for backwards compatibility.
   */
  includeCount?: boolean;
  /**
   * SQL-only action scan: return mapped rows without `applyActionFilterToRows`.
   */
  skipActionClientFilter?: boolean;
}

export interface GetPbhOrdersResponse<T = unknown> {
  data: T[];
  /** Omitted when includeCount was false */
  count?: number;
  /**
   * Action-filter list: true once the full in-memory list is loaded.
   */
  scanComplete?: boolean;
}

/**
 * Build order query options for orderQueryODataSqlOnly
 * Handles complex logic for combining property/query filters with status filters.
 * Note: Only one status filter (inbound OR outbound) can be active at a time.
 */
export const buildPbhOrdersSqlOnlyQueryOptions = (
  args: GetPbhOrdersArgs & { signal?: AbortSignal },
): {
  property?: string;
  query?: string;
  rows: number;
  skip: number;
  /**
   * Display label for an inbound or outbound status combined with property/query.
   * Passed to `orderQueryODataSqlOnly` (not necessarily an outbound status).
   */
  inOrOutStatusFilterLabel?: string;
  action?: { name: string; user?: Record<string, unknown> };
  includeCount?: boolean;
  skipActionClientFilter?: boolean;
  signal?: AbortSignal;
} => {
  const options: {
    property?: string;
    query?: string;
    rows: number;
    skip: number;
    inOrOutStatusFilterLabel?: string;
    action?: { name: string; user?: Record<string, unknown> };
    includeCount?: boolean;
    skipActionClientFilter?: boolean;
    signal?: AbortSignal;
  } = {
    rows: args.rows,
    skip: args.skip,
    includeCount: args.includeCount !== false,
  };

  if (!args.property || !args.query) {
    if (args.inboundStatus) {
      options.property = "InboundStatus";
      options.query = args.inboundStatus;
    } else if (args.outboundStatus) {
      options.property = "OutboundStatus";
      options.query = args.outboundStatus;
    }
  } else {
    options.property = args.property;
    options.query = args.query;

    // Combined text + status: orderQueryODataSqlOnly reads inOrOutStatusFilterLabel.
    if (args.inboundStatus) {
      options.inOrOutStatusFilterLabel = args.inboundStatus;
    } else if (args.outboundStatus) {
      options.inOrOutStatusFilterLabel = args.outboundStatus;
    }
  }

  if (args.action) {
    options.action = {
      name: args.action,
      ...(args.user ? { user: args.user } : {}),
    };
  }

  if (args.skipActionClientFilter) {
    options.skipActionClientFilter = true;
  }

  if (args.signal) {
    options.signal = args.signal;
  }

  return options;
};

export type PbhOrdersSqlOnlyOrderQueryFn = (
  options: ReturnType<typeof buildPbhOrdersSqlOnlyQueryOptions>,
  sort: PbhSort | undefined,
  entireDataSet: boolean,
) => Promise<{ data?: unknown[]; count?: number; odataRowCount?: number }>;
